import { useEffect, useState, useCallback, useMemo } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Calendar as CalendarIcon, Tv as TvIcon, Clock } from "lucide-react";
import { toast } from "sonner";
import NYClock from "@/components/NYClock";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOUR_HEIGHT = 56; // pixels per hour (column scrollable height = 24 * 56 = 1344)
const HOURS = Array.from({ length: 24 }, (_, i) => i);
// Deterministic palette so the same TV always gets the same color
const TV_PALETTE = [
  { bg: "bg-blue-500/15",    border: "border-blue-500",    text: "text-blue-900",    dot: "bg-blue-500"    },
  { bg: "bg-emerald-500/15", border: "border-emerald-500", text: "text-emerald-900", dot: "bg-emerald-500" },
  { bg: "bg-amber-500/15",   border: "border-amber-500",   text: "text-amber-900",   dot: "bg-amber-500"   },
  { bg: "bg-purple-500/15",  border: "border-purple-500",  text: "text-purple-900",  dot: "bg-purple-500"  },
  { bg: "bg-rose-500/15",    border: "border-rose-500",    text: "text-rose-900",    dot: "bg-rose-500"    },
  { bg: "bg-cyan-500/15",    border: "border-cyan-500",    text: "text-cyan-900",    dot: "bg-cyan-500"    },
  { bg: "bg-orange-500/15",  border: "border-orange-500",  text: "text-orange-900",  dot: "bg-orange-500"  },
  { bg: "bg-indigo-500/15",  border: "border-indigo-500",  text: "text-indigo-900",  dot: "bg-indigo-500"  },
];
const colorForTv = (tvIndex) => TV_PALETTE[tvIndex % TV_PALETTE.length];

function fmt12h(minutes) {
  const m = ((minutes % 1440) + 1440) % 1440;
  const h24 = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, "0");
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm}${period.toLowerCase()}`;
}

export default function SchedulesCalendarPage() {
  const { activeBusiness } = useBusiness();
  const [tvs, setTvs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [visibleTvIds, setVisibleTvIds] = useState(null); // null = all
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        apiAdmin.get(`/tvs?business_id=${activeBusiness.id}`),
        apiAdmin.get(`/playlists?business_id=${activeBusiness.id}`),
      ]);
      setTvs(t.data);
      setPlaylists(p.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [activeBusiness]);

  useEffect(() => { load(); }, [load]);

  const playlistById = useMemo(
    () => Object.fromEntries(playlists.map((p) => [p.id, p])),
    [playlists]
  );

  // Build a flat list of [tv, schedule, expandedDay] blocks, exploded per day
  const blocks = useMemo(() => {
    const out = [];
    tvs.forEach((tv, tvIdx) => {
      if (visibleTvIds && !visibleTvIds.has(tv.id)) return;
      const color = colorForTv(tvIdx);
      (tv.schedules || []).forEach((s) => {
        if (!s.enabled) return;
        const pl = playlistById[s.playlist_id];
        if (!pl) return; // skip schedules whose playlist was deleted
        (s.days_of_week || []).forEach((day) => {
          // Handle overnight (start > end) by splitting into two segments
          if (s.start_minute <= s.end_minute) {
            out.push(makeBlock(tv, tvIdx, color, s, pl, day, s.start_minute, s.end_minute, false));
          } else {
            out.push(makeBlock(tv, tvIdx, color, s, pl, day, s.start_minute, 1440, false));
            const nextDay = (day + 1) % 7;
            out.push(makeBlock(tv, tvIdx, color, s, pl, nextDay, 0, s.end_minute, true));
          }
        });
      });
    });
    return out;
  }, [tvs, playlistById, visibleTvIds]);

  // Group blocks by day for rendering — and within a day, by overlapping cluster for side-by-side
  const blocksByDay = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    blocks.forEach((b) => map[b.day].push(b));
    return map.map((dayBlocks) => layoutBlocks(dayBlocks));
  }, [blocks]);

  const totalSlots = blocks.length;
  const tvsWithSchedules = tvs.filter((t) => (t.schedules || []).some((s) => s.enabled)).length;

  return (
    <div className="space-y-5" data-testid="schedules-calendar-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Programming</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Schedule Calendar
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Weekly view of every scheduled playlist across all your TVs · America/New_York.
          </p>
        </div>
        <div className="text-sm text-slate-600 flex items-center gap-3 flex-wrap">
          <NYClock />
          <span className="inline-flex items-center gap-1.5">
            <CalendarIcon className="h-3.5 w-3.5 text-slate-400" /> {totalSlots} scheduled blocks
          </span>
          <span className="inline-flex items-center gap-1.5">
            <TvIcon className="h-3.5 w-3.5 text-slate-400" /> {tvsWithSchedules} of {tvs.length} TVs
          </span>
        </div>
      </div>

      {/* TV filter chips */}
      {tvs.length > 0 && (
        <div
          className="rounded-2xl bg-white border border-slate-200 p-3 flex items-center gap-2 flex-wrap"
          data-testid="schedules-tv-filters"
        >
          <button
            type="button"
            onClick={() => setVisibleTvIds(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              visibleTvIds === null
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="schedules-filter-all"
          >
            All TVs
          </button>
          {tvs.map((t, idx) => {
            const color = colorForTv(idx);
            const isVisible = visibleTvIds === null || visibleTvIds.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setVisibleTvIds((prev) => {
                    if (prev === null) return new Set(tvs.filter((x) => x.id !== t.id).map((x) => x.id));
                    const next = new Set(prev);
                    next.has(t.id) ? next.delete(t.id) : next.add(t.id);
                    return next;
                  });
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  isVisible
                    ? "border-slate-300 bg-white text-slate-800"
                    : "border-slate-200 bg-slate-50 text-slate-400 line-through"
                }`}
                data-testid={`schedules-filter-tv-${t.id}`}
              >
                <span className={`h-2 w-2 rounded-full ${color.dot}`} />
                {t.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Calendar grid */}
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        {/* Day headers */}
        <div className="grid border-b border-slate-100" style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}>
          <div className="bg-slate-50/60" />
          {DAYS.map((d) => (
            <div
              key={d}
              className="px-3 py-2.5 text-center text-[11px] uppercase tracking-widest font-medium text-slate-500 bg-slate-50/60 border-l border-slate-100"
              data-testid={`schedules-day-header-${d.toLowerCase()}`}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Body — hours column + 7 day columns with absolute-positioned blocks */}
        <div className="grid relative" style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}>
          {/* Hours column (left) */}
          <div className="bg-slate-50/40 border-r border-slate-100">
            {HOURS.map((h) => (
              <div
                key={h}
                className="text-[10px] text-slate-400 text-right pr-2 font-mono"
                style={{ height: HOUR_HEIGHT, paddingTop: 2 }}
              >
                {h === 0 ? "" : `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "a" : "p"}`}
              </div>
            ))}
          </div>

          {/* 7 day columns */}
          {DAYS.map((dayName, dayIdx) => (
            <div
              key={dayName}
              className="relative border-l border-slate-100"
              style={{ height: HOUR_HEIGHT * 24 }}
              data-testid={`schedules-day-${dayName.toLowerCase()}`}
            >
              {/* Hourly grid lines */}
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="absolute inset-x-0 border-t border-slate-50"
                  style={{ top: h * HOUR_HEIGHT }}
                />
              ))}

              {/* Schedule blocks */}
              {blocksByDay[dayIdx].map((b) => (
                <ScheduleBlock key={b.key} block={b} />
              ))}
            </div>
          ))}
        </div>
      </div>

      {blocks.length === 0 && !loading && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Clock className="h-10 w-10 mx-auto text-slate-300 mb-3" strokeWidth={1.25} />
          <div className="font-heading text-lg font-semibold text-slate-900">No schedules yet</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Create a playlist and toggle "Schedule when this playlist plays" to see it here. You can also add schedule slots directly from any TV's row.
          </p>
        </div>
      )}
    </div>
  );
}

/* --------- Block component --------- */
function ScheduleBlock({ block }) {
  const minutesPerPx = 60 / HOUR_HEIGHT;
  const top = block.start / minutesPerPx;
  const height = Math.max(20, (block.end - block.start) / minutesPerPx - 2);
  const widthPct = 100 / block.totalLanes;
  const leftPct = block.lane * widthPct;
  return (
    <div
      className={`absolute rounded-lg border-l-4 ${block.color.bg} ${block.color.border} px-2 py-1 overflow-hidden hover:shadow-md hover:z-10 transition-shadow cursor-default`}
      style={{
        top, height,
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
      }}
      title={`${block.tvName} · ${block.label || "Slot"} · ${fmt12h(block.originalStart)} – ${fmt12h(block.originalEnd)}`}
      data-testid={`schedules-block-${block.key}`}
    >
      <div className={`text-[10px] font-semibold leading-tight truncate ${block.color.text}`}>
        {block.playlistName || "(deleted playlist)"}
      </div>
      <div className="text-[9px] text-slate-600 leading-tight truncate">
        {fmt12h(block.originalStart)}–{fmt12h(block.originalEnd)}
      </div>
      <div className="text-[9px] text-slate-500 leading-tight truncate flex items-center gap-1">
        <span className={`h-1.5 w-1.5 rounded-full ${block.color.dot}`} />
        {block.tvName}{block.isOvernightTail ? " (cont.)" : ""}
      </div>
      {block.label && (
        <div className="text-[9px] text-slate-400 italic leading-tight truncate">
          {block.label}
        </div>
      )}
    </div>
  );
}

/* --------- Helpers --------- */
function makeBlock(tv, tvIdx, color, schedule, playlist, day, start, end, isOvernightTail) {
  return {
    key: `${tv.id}-${schedule.id}-${day}-${start}-${end}`,
    tvId: tv.id,
    tvName: tv.name,
    tvIdx,
    color,
    label: schedule.label || "",
    playlistName: playlist?.name,
    day,
    start,
    end,
    originalStart: schedule.start_minute,
    originalEnd: schedule.end_minute,
    isOvernightTail,
    lane: 0,
    totalLanes: 1,
  };
}

/** Lay out overlapping blocks side-by-side within a day column. */
function layoutBlocks(dayBlocks) {
  // Sort by start time
  dayBlocks.sort((a, b) => a.start - b.start || a.end - b.end);
  // Greedy lane assignment: find the first lane whose last block ends ≤ current block start
  const laneEnds = [];
  dayBlocks.forEach((b) => {
    let assigned = false;
    for (let i = 0; i < laneEnds.length; i++) {
      if (laneEnds[i] <= b.start) {
        b.lane = i;
        laneEnds[i] = b.end;
        assigned = true;
        break;
      }
    }
    if (!assigned) {
      b.lane = laneEnds.length;
      laneEnds.push(b.end);
    }
  });
  const totalLanes = Math.max(1, laneEnds.length);
  dayBlocks.forEach((b) => { b.totalLanes = totalLanes; });
  return dayBlocks;
}
